import { useCallback, useEffect, useState } from "react";
import { readPref, writePref } from "../utils/safeStorage";

export const SIDEBAR_WIDTH_DEFAULT = 260;
const SIDEBAR_WIDTH_MIN = 180;
const SIDEBAR_WIDTH_MAX = 400;

/**
 * The admin sidebar's collapsed/expanded state and width, per user — keyed by
 * email so two people sharing a browser each keep their own (falls back to
 * "default" before the user is known). The width is also published as
 * --sidebar-width, which `.sidebar.open` and the `.main-wrapper` margin use.
 * Storage goes through utils/safeStorage (Safari "Block All Cookies" can't crash the frame).
 */
export default function useSidebarPrefs(email) {
  const key = (suffix) => `chariot-sidebar-${suffix}:${email || "default"}`;
  const [sidebarOpen, setSidebarOpen] = useState(() => readPref(key("open")) === "true");
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = parseInt(readPref(key("width")), 10);
    return Number.isFinite(saved) ? saved : SIDEBAR_WIDTH_DEFAULT;
  });
  const [sidebarResizing, setSidebarResizing] = useState(false);

  useEffect(() => {
    writePref(key("open"), String(sidebarOpen));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarOpen, email]);

  useEffect(() => {
    document.documentElement.style.setProperty("--sidebar-width", `${sidebarWidth}px`);
    writePref(key("width"), String(sidebarWidth));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarWidth, email]);

  // Drag-to-resize handle. Bounded between MIN and MAX; persists on release.
  const handleSidebarResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = sidebarWidth;
      setSidebarResizing(true);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const handleMove = (ev) => {
        const dx = ev.clientX - startX;
        setSidebarWidth(Math.max(SIDEBAR_WIDTH_MIN, Math.min(SIDEBAR_WIDTH_MAX, startWidth + dx)));
      };
      const handleUp = () => {
        window.removeEventListener("mousemove", handleMove);
        window.removeEventListener("mouseup", handleUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        setSidebarResizing(false);
      };
      window.addEventListener("mousemove", handleMove);
      window.addEventListener("mouseup", handleUp);
    },
    [sidebarWidth]
  );

  return { sidebarOpen, setSidebarOpen, sidebarWidth, setSidebarWidth, sidebarResizing, handleSidebarResizeStart };
}
